import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    number: '01',
    tag: 'AI 效能新紀元',
    title: 'AI 效能新紀元',
    body: '如果說短影音引爆了「流量」的時代革命，那麼 AI 正在掀起一場「效能」的劃時代巨變。這不僅是一場媲美 18、19 世紀的工業革命，其顛覆世界與演進的速度，將比過去任何一次變革都更加迅猛。',
    icon: 'ri-flashlight-line',
    accent: 'from-amber-400 to-yellow-300',
    glow: 'rgba(251,191,36,0.25)',
  },
  {
    number: '02',
    tag: '全方位效能大爆發',
    title: '你來當大腦，剩下的交給 AI',
    body: '無論是影片、圖片、簡報、文案、音樂還是企劃，這些繁瑣的執行都能由 AI 為你一手包辦。從此不再需要親力親為，你只需要提供核心想法與最終審核，成為掌控全局的老闆。',
    icon: 'ri-brain-line',
    accent: 'from-amber-300 to-orange-300',
    glow: 'rgba(251,191,36,0.2)',
  },
  {
    number: '03',
    tag: '老闆的破局思維',
    title: '老闆的破局思維',
    body: '別人還在承擔「與人協作」的漫長週期與成本，我們已全面導入「與 AI 協作」的高效工作。無論是企劃、製圖、影片還是後製，打破時間壁壘，最短一天即可完成最高品質的交付。',
    icon: 'ri-rocket-2-line',
    accent: 'from-yellow-300 to-amber-500',
    glow: 'rgba(251,191,36,0.3)',
  },
];

function StepCard({ step, index }: { step: typeof steps[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="relative flex flex-col lg:flex-row items-start gap-8 lg:gap-16"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.7s ease ${index * 0.18}s, transform 0.7s ease ${index * 0.18}s`,
      }}
    >
      {/* Left: number + connector */}
      <div className="flex flex-col items-center flex-shrink-0 w-20">
        {/* Number circle */}
        <div
          className="relative w-20 h-20 flex items-center justify-center rounded-full border border-amber-500/40 bg-black"
          style={{ boxShadow: `0 0 32px ${step.glow}` }}
        >
          <span
            className={`text-2xl font-black bg-gradient-to-br ${step.accent} bg-clip-text text-transparent`}
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            {step.number}
          </span>
        </div>
        {/* Connector line (not on last) */}
        {index < steps.length - 1 && (
          <div className="w-px flex-1 min-h-[80px] mt-4 bg-gradient-to-b from-amber-500/40 to-transparent" />
        )}
      </div>

      {/* Right: card */}
      <div
        className="flex-1 mb-12 lg:mb-16 rounded-sm border border-amber-500/20 bg-gradient-to-br from-white/[0.03] to-transparent p-8 lg:p-10 hover:border-amber-400/40 transition-all duration-500 group"
        style={{ backdropFilter: 'blur(4px)' }}
      >
        {/* Tag + icon */}
        <div className="flex items-center gap-4 mb-5">
          <div
            className="w-10 h-10 flex items-center justify-center rounded-sm border border-amber-500/30 bg-amber-500/10 group-hover:bg-amber-500/20 transition-all duration-300"
          >
            <i className={`${step.icon} text-xl text-amber-400`} />
          </div>
          <span
            className="text-amber-400/70 text-xs tracking-[0.35em] uppercase"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            {step.tag}
          </span>
        </div>

        {/* Title */}
        <h3
          className="text-2xl lg:text-3xl font-black text-white mb-4 leading-snug"
          style={{ fontFamily: "'Noto Serif TC', serif" }}
        >
          {step.title}
        </h3>

        {/* Body */}
        <p
          className="text-white/70 text-sm lg:text-base leading-relaxed"
          style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
        >
          {step.body}
        </p>

        {/* Bottom accent line */}
        <div
          className={`mt-6 h-px w-0 group-hover:w-full bg-gradient-to-r ${step.accent} transition-all duration-700`}
        />
      </div>
    </div>
  );
}

export default function EraBonus() {
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setTitleVisible(true);
      },
      { threshold: 0.3 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="era-bonus" className="relative py-32 px-8 bg-black overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-amber-950/8 to-black pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[1px] bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
      {/* Ambient glow */}
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-amber-500/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-1/3 left-0 w-[400px] h-[400px] bg-amber-600/5 blur-[100px] rounded-full pointer-events-none" />

      <div className="relative z-10 max-w-5xl mx-auto">
        {/* Section header */}
        <div
          ref={titleRef}
          className="text-center mb-20"
          style={{
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-400" />
            <span
              className="text-amber-400/80 text-xs tracking-[0.4em] font-light uppercase"
              style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
            >
              ERA ADVANTAGE
            </span>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-amber-400" />
          </div>

          <h2
            className="text-4xl md:text-6xl font-black leading-tight tracking-tight mb-6"
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            <span
              style={{
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 30%, #fde68a 60%, #d97706 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: 'drop-shadow(0 0 30px rgba(251,191,36,0.4))',
              }}
            >
              你不能錯過的時代紅利
            </span>
          </h2>

          <p
            className="text-white/65 text-sm tracking-widest"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            三個關鍵洞察，帶你看清 AI 時代的真正機會
          </p>
        </div>

        {/* Steps */}
        <div>
          {steps.map((step, index) => (
            <StepCard key={step.number} step={step} index={index} />
          ))}
        </div>


      </div>
    </section>
  );
}
